import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  Scissors, ChevronRight, ChevronLeft, CheckCircle2, Calendar, 
  Home, Plus, ExternalLink, AlertCircle, BookOpen, Play
} from 'lucide-react';
import { SPLIT_METHODS, SAME_HIVE_METHODS, createSplitReminders } from '../../utils/splitMethods';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const SplitWizard = ({ open, onOpenChange, hive, hives = [], yards = [], onSuccess }) => {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Destination hive selection
  const [destinationType, setDestinationType] = useState('ready'); // 'ready' or 'new'
  const [selectedReadyHive, setSelectedReadyHive] = useState('');
  const [newHiveData, setNewHiveData] = useState({
    hive_number: '',
    nickname: '',
    yard: ''
  });
  
  // Get available "Ready" hives
  const readyHives = hives.filter(h => h.status === 'ready' && h.id !== hive?.id);
  
  useEffect(() => {
    if (open) {
      // Reset state when dialog opens
      setSelectedMethod(null);
      setCurrentStep(0);
      setCompletedSteps({});
      setNotes('');
      setDestinationType(readyHives.length > 0 ? 'ready' : 'new');
      setSelectedReadyHive('');
      setNewHiveData({
        hive_number: Math.max(0, ...hives.map(h => h.hive_number || 0)) + 1,
        nickname: '',
        yard: hive?.yard || yards[0]?.name || ''
      });
    }
  }, [open]);

  const method = SPLIT_METHODS.find(m => m.id === selectedMethod);
  const canStayInHive = SAME_HIVE_METHODS.includes(selectedMethod);

  useEffect(() => {
    if (canStayInHive) setDestinationType('same');
    else if (destinationType === 'same') setDestinationType(readyHives.length > 0 ? 'ready' : 'new');
  }, [selectedMethod]);

  const toggleStepComplete = (stepIndex, itemIndex) => {
    const key = `${stepIndex}-${itemIndex}`;
    setCompletedSteps(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isStepFullyComplete = (stepIndex) => {
    if (!method) return false;
    const step = method.steps[stepIndex];
    return step.checklist.every((_, itemIndex) => completedSteps[`${stepIndex}-${itemIndex}`]);
  };

  const handleStartSplit = async () => {
    if (!method) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let destinationHiveId = null;
      let destinationHiveNumber = null;
      
      if (destinationType === 'same') {
        destinationHiveId = hive?.id;
        destinationHiveNumber = hive?.hive_number;
      } else if (destinationType === 'new') {
        const hiveResponse = await axios.post(`${API}/hives`, {
          hive_number: parseInt(newHiveData.hive_number),
          nickname: newHiveData.nickname || `Split from H${hive?.hive_number}`,
          yard: newHiveData.yard,
          status: 'active',
          origin_type: `Split (${method.name})`,
          origin_date: new Date().toISOString().split('T')[0],
          is_split: true,
          split_from_hive: hive?.id || '',
          split_type: method.id,
          notes: `Created via ${method.name} split from Hive ${hive?.hive_number}`,
          hive_stack: hive?.hive_stack || { hive_type: 'langstroth' }
        }, { headers });
        destinationHiveId = hiveResponse.data.id;
        destinationHiveNumber = newHiveData.hive_number;
      } else {
        // Use selected Ready hive
        destinationHiveId = selectedReadyHive;
        const readyHive = readyHives.find(h => h.id === selectedReadyHive);
        destinationHiveNumber = readyHive?.hive_number;
        
        // Activate the Ready hive with split information
        await axios.put(`${API}/hives/${selectedReadyHive}`, {
          ...readyHive,
          status: 'active',
          origin_type: `Split (${method.name})`,
          origin_date: new Date().toISOString().split('T')[0],
          is_split: true,
          split_from_hive: hive?.id || '',
          split_type: method.id,
          notes: `${readyHive?.notes || ''} - Activated via ${method.name} split from Hive ${hive?.hive_number}`
        }, { headers });
      }
      
      // Create split record
      const splitRecord = {
        parent_hive_id: hive?.id,
        parent_hive_number: hive?.hive_number,
        destination_hive_id: destinationHiveId,
        destination_hive_number: destinationHiveNumber,
        split_method: method.id,
        split_method_name: method.name,
        start_date: new Date().toISOString(),
        status: 'in_progress',
        current_step: 0,
        total_steps: method.steps.length,
        done_steps: [],
        notes: notes,
        completed_steps: completedSteps
      };
      
      const splitRes = await axios.post(`${API}/hive-splits`, splitRecord, { headers });
      
      // Reminders go to Tasks & Calendar on the hive that holds the split
      const reminderHive = destinationType === 'same' ? hive
        : destinationType === 'new' ? { hive_number: parseInt(newHiveData.hive_number), yard: newHiveData.yard }
        : readyHives.find(h => h.id === selectedReadyHive);
      const created = await createSplitReminders({ method, hiveId: destinationHiveId, hiveNumber: reminderHive?.hive_number ?? destinationHiveNumber,
        yard: reminderHive?.yard, splitId: splitRes.data?.id, headers });
      
      const where = destinationType === 'same' ? `on Hive ${destinationHiveNumber} (kept in the same hive)` : `— Hive ${destinationHiveNumber} is ready`;
      toast.success(`${method.name} started ${where}.`, { description: created ? `${created} reminder${created !== 1 ? 's' : ''} added to Tasks and Calendar.` : undefined });
      window.dispatchEvent(new Event('refreshData'));
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to start split');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMethodSelection = () => (
    <div className="space-y-4">
      <p className="text-sm text-stone-600">
        Select a split method for <strong>Hive {hive?.hive_number}</strong>
        {hive?.nickname && ` "${hive.nickname}"`}
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {SPLIT_METHODS.map(m => (
          <button
            key={m.id}
            onClick={() => setSelectedMethod(m.id)}
            className={`${m.color} text-white p-4 rounded-xl text-left transition-all hover:scale-[1.02] hover:shadow-lg`}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="font-bold text-lg">{m.name}</div>
                <div className="text-sm opacity-90 mt-1">{m.description}</div>
              </div>
              <Scissors className="h-5 w-5 opacity-80" />
            </div>
            <div className="flex gap-3 mt-3 text-xs">
              <span className="bg-white/20 px-2 py-1 rounded">{m.difficulty}</span>
              <span className="bg-white/20 px-2 py-1 rounded">{m.duration}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderDestinationSelection = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => setSelectedMethod(null)}>
          ← Back
        </Button>
        <span className="font-semibold text-lg">{method?.name}</span>
      </div>
      
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h3 className="font-semibold text-amber-800 mb-2">Destination Hive</h3>
        <p className="text-sm text-amber-700 mb-4">
          Where will the split colony go?
        </p>
        
        <div className="space-y-3">
          {canStayInHive && (
            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
              destinationType === 'same' ? 'border-amber-500 bg-amber-100' : 'border-stone-200 hover:border-stone-300'
            }`} data-testid="split-destination-same">
              <input
                type="radio"
                name="destinationType"
                checked={destinationType === 'same'}
                onChange={() => setDestinationType('same')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Keep in Hive {hive?.hive_number}: boost this colony</div>
                <div className="text-sm text-stone-600 mt-1">
                  No new hive. Everything stays in the same stack and reminders go on this hive.
                </div>
              </div>
            </label>
          )}
          {readyHives.length > 0 && (
            <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
              destinationType === 'ready' ? 'border-amber-500 bg-amber-100' : 'border-stone-200 hover:border-stone-300'
            }`}>
              <input
                type="radio"
                name="destinationType"
                checked={destinationType === 'ready'}
                onChange={() => setDestinationType('ready')}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium">Use Existing "Ready" Hive</div>
                <div className="text-sm text-stone-600 mt-1">
                  Select from {readyHives.length} prepared hive(s)
                </div>
                {destinationType === 'ready' && (
                  <Select value={selectedReadyHive} onValueChange={setSelectedReadyHive}>
                    <SelectTrigger className="mt-2 bg-white">
                      <SelectValue placeholder="Select a ready hive" />
                    </SelectTrigger>
                    <SelectContent>
                      {readyHives.map(h => (
                        <SelectItem key={h.id} value={h.id}>
                          Hive {h.hive_number} {h.nickname && `"${h.nickname}"`} - {h.yard || 'No yard'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </label>
          )}
          
          <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
            destinationType === 'new' ? 'border-amber-500 bg-amber-100' : 'border-stone-200 hover:border-stone-300'
          }`}>
            <input
              type="radio"
              name="destinationType"
              checked={destinationType === 'new'}
              onChange={() => setDestinationType('new')}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="font-medium flex items-center gap-2">
                <Plus className="h-4 w-4" /> Create New Hive
              </div>
              <div className="text-sm text-stone-600 mt-1">
                Create a new hive record for the split
              </div>
              {destinationType === 'new' && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Hive Number</Label>
                    <Input
                      type="number"
                      value={newHiveData.hive_number}
                      onChange={(e) => setNewHiveData({...newHiveData, hive_number: e.target.value})}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nickname (optional)</Label>
                    <Input
                      value={newHiveData.nickname}
                      onChange={(e) => setNewHiveData({...newHiveData, nickname: e.target.value})}
                      placeholder={`Split from H${hive?.hive_number}`}
                      className="bg-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Yard/Apiary</Label>
                    <Select value={newHiveData.yard} onValueChange={(v) => setNewHiveData({...newHiveData, yard: v})}>
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select yard" />
                      </SelectTrigger>
                      <SelectContent>
                        {yards.map(y => (
                          <SelectItem key={y.id} value={y.name}>{y.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Notes (optional)</Label>
        <Textarea
          placeholder="Any notes about this split..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          View Step-by-Step Guide
          <BookOpen className="h-4 w-4 ml-2" />
        </Button>
        <Button
          onClick={handleStartSplit}
          disabled={isSubmitting || (destinationType === 'ready' && !selectedReadyHive)}
          className="bg-green-500 hover:bg-green-600"
        >
          {isSubmitting ? 'Creating...' : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Start Split
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderStepGuide = () => {
    if (!method) return null;
    const step = method.steps[currentStep];
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setCurrentStep(0)}>
            ← Back to Setup
          </Button>
          <a 
            href={method.videoUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            Watch Videos <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        
        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          {method.steps.map((s, i) => (
            <div
              key={i}
              className={`flex-1 h-2 rounded-full ${
                i < currentStep ? 'bg-green-500' :
                i === currentStep ? 'bg-amber-500' :
                'bg-stone-200'
              }`}
            />
          ))}
        </div>
        
        <div className={`${method.color} text-white rounded-xl p-4`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="text-sm opacity-80">Step {currentStep + 1} of {method.steps.length}</div>
              <div className="font-bold text-xl mt-1">{step.title}</div>
            </div>
            {step.timing && (
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {step.timing}
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-stone-50 rounded-lg p-4">
          <h4 className="font-semibold mb-2">Instructions</h4>
          <p className="text-stone-700">{step.instructions}</p>
        </div>
        
        <div className="bg-white border rounded-lg p-4">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Checklist
          </h4>
          <div className="space-y-2">
            {step.checklist.map((item, i) => (
              <label 
                key={i}
                className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-stone-50 ${
                  completedSteps[`${currentStep}-${i}`] ? 'bg-green-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={completedSteps[`${currentStep}-${i}`] || false}
                  onChange={() => toggleStepComplete(currentStep, i)}
                  className="h-5 w-5 rounded"
                />
                <span className={completedSteps[`${currentStep}-${i}`] ? 'line-through text-stone-500' : ''}>
                  {item}
                </span>
              </label>
            ))}
          </div>
        </div>
        
        {step.createReminder && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <div className="font-medium text-blue-800">Calendar Reminder</div>
              <div className="text-sm text-blue-700">
                A reminder will be created for this step when you start the split.
              </div>
            </div>
          </div>
        )}
        
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          
          {currentStep < method.steps.length - 1 ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentStep(0)}
              className="bg-green-500 hover:bg-green-600"
            >
              Back to Start Split
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5 text-amber-600" />
            Split Wizard
            {method && ` - ${method.name}`}
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {!selectedMethod && renderMethodSelection()}
          {selectedMethod && currentStep === 0 && renderDestinationSelection()}
          {selectedMethod && currentStep > 0 && renderStepGuide()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SplitWizard;
